// Port of CoreModels/Geo/SwissCoordinates.swift: approximate WGS84 ↔ LV95 (EPSG:2056) with the published swisstopo
// formulas (~1 m), the inverse refined by Newton iterations on the forward formula (round trip < 1 mm).

import type { GeoCoordinate } from './models';
import { roundHalfAwayFromZero } from './polyline';

export interface LV95Coordinate {
  easting: number;
  northing: number;
}

export function isPlausibleLV95(lv: LV95Coordinate): boolean {
  return lv.easting >= 2_400_000 && lv.easting <= 2_900_000 && lv.northing >= 1_050_000 && lv.northing <= 1_320_000;
}

function auxiliary(c: GeoCoordinate): { phi: number; lambda: number } {
  return { phi: (c.latitude * 3600 - 169_028.66) / 10_000, lambda: (c.longitude * 3600 - 26_782.5) / 10_000 };
}

function forward(phi: number, lambda: number): { easting: number; northing: number } {
  const easting =
    2_600_072.37 + 211_455.93 * lambda - 10_938.51 * lambda * phi - 0.36 * lambda * phi * phi - 44.54 * lambda ** 3;
  const northing =
    1_200_147.07 +
    308_807.95 * phi +
    3_745.25 * lambda * lambda +
    76.63 * phi * phi -
    194.56 * lambda * lambda * phi +
    119.79 * phi ** 3;
  return { easting, northing };
}

export function lv95FromWGS84(c: GeoCoordinate): LV95Coordinate {
  const { phi, lambda } = auxiliary(c);
  return forward(phi, lambda);
}

export function wgs84FromLV95(lv: LV95Coordinate): GeoCoordinate {
  const y = (lv.easting - 2_600_000) / 1_000_000;
  const x = (lv.northing - 1_200_000) / 1_000_000;
  const lambda0 = 2.6779094 + 4.728982 * y + 0.791484 * y * x + 0.1306 * y * x * x - 0.0436 * y ** 3;
  const phi0 = 16.9023892 + 3.238272 * x - 0.270978 * y * y - 0.002528 * x * x - 0.0447 * y * y * x - 0.014 * x ** 3;
  const start: GeoCoordinate = { latitude: (phi0 * 100) / 36, longitude: (lambda0 * 100) / 36 };
  if (!Number.isFinite(lv.easting) || !Number.isFinite(lv.northing)) return start;
  let { phi, lambda } = auxiliary(start);
  for (let i = 0; i < 5; i++) {
    const f = forward(phi, lambda);
    const rE = lv.easting - f.easting;
    const rN = lv.northing - f.northing;
    if (Math.abs(rE) < 1e-6 && Math.abs(rN) < 1e-6) break;
    const dEdPhi = -10_938.51 * lambda - 0.72 * lambda * phi;
    const dEdLambda = 211_455.93 - 10_938.51 * phi - 0.36 * phi * phi - 133.62 * lambda * lambda;
    const dNdPhi = 308_807.95 + 153.26 * phi - 194.56 * lambda * lambda + 359.37 * phi * phi;
    const dNdLambda = 7_490.5 * lambda - 389.12 * lambda * phi;
    const det = dEdPhi * dNdLambda - dEdLambda * dNdPhi;
    if (!Number.isFinite(det) || Math.abs(det) <= 1e-9) break;
    phi += (rE * dNdLambda - rN * dEdLambda) / det;
    lambda += (rN * dEdPhi - rE * dNdPhi) / det;
  }
  const refined = { latitude: (phi * 10_000 + 169_028.66) / 3600, longitude: (lambda * 10_000 + 26_782.5) / 3600 };
  return Number.isFinite(refined.latitude) && Number.isFinite(refined.longitude) ? refined : start;
}

function groupThousands(value: number): string {
  const s = String(Math.abs(value));
  let out = '';
  for (let i = 0; i < s.length; i++) {
    if (i > 0 && (s.length - i) % 3 === 0) out += "'";
    out += s[i];
  }
  return (value < 0 ? '-' : '') + out;
}

/** Formats LV95 as swisstopo does, e.g. "2'645'123, 1'158'456". */
export function formatLV95(lv: LV95Coordinate): string {
  return `${groupThousands(roundHalfAwayFromZero(lv.easting))}, ${groupThousands(roundHalfAwayFromZero(lv.northing))}`;
}
